import type { AcpAgentCatalogEntry } from './acp-agent-catalog';

/** Who owns the core's own agents in the catalog (never an installed plugin). */
export const CORE_AGENT_OWNER = 'crowdex';

/**
 * The agents the core runs natively (Claude Code, Codex, OpenCode): their native conversation
 * backends live in the CLI, and these declarations describe their ACP launch as a fallback.
 * Other agents come from plugins.
 */
export const CORE_ACP_AGENTS = [
  {
    id: 'claude-acp',
    localId: 'auto-claude-code',
    name: 'Claude Code',
    family: 'claude-code',
    default: true,
    aliases: ['claude'],
    discovery: {
      binaries: ['claude-agent-acp', 'claude-code-acp'],
    },
    launch: {
      args: [],
      npm: { package: '@agentclientprotocol/claude-agent-acp', bin: 'claude-agent-acp' },
    },
    model: {
      target: 'env',
      name: 'ANTHROPIC_MODEL',
    },
    modelCompatibility: {
      includePrefixes: ['claude-'],
    },
    sessionConfig: [
      {
        aliases: ['reasoningEffort', 'thought_level', 'reasoning_effort', 'effort'],
        target: 'effort',
        choices: ['low', 'medium', 'high', 'xhigh', 'max'],
      },
    ],
    providerInjection: {
      baseUrl: {
        target: 'env',
        name: 'ANTHROPIC_BASE_URL',
      },
      auth: {
        target: 'env',
        name: 'ANTHROPIC_AUTH_TOKEN',
      },
    },
    capabilities: ['models', 'reasoning', 'delegation'],
    signIn: { command: 'claude /login' },
    plugin: CORE_AGENT_OWNER,
  },
  {
    id: 'codex-acp',
    localId: 'auto-codex',
    name: 'Codex',
    family: 'codex',
    default: true,
    aliases: ['codex'],
    discovery: {
      binaries: ['codex-acp'],
    },
    launch: {
      args: [],
      npm: { package: '@agentclientprotocol/codex-acp', bin: 'codex-acp' },
      // The adapter drives the Codex the host runs natively, not its own older dependency.
      env: {
        CODEX_PATH: 'codex',
      },
    },
    model: {
      target: 'session',
    },
    modelCompatibility: {
      excludePrefixes: ['claude-'],
    },
    sessionConfig: [
      {
        aliases: ['reasoningEffort', 'thought_level', 'reasoning_effort', 'effort'],
        target: 'reasoning_effort',
        choices: ['low', 'medium', 'high'],
      },
    ],
    providerInjection: {
      baseUrl: {
        target: 'env',
        name: 'OPENAI_BASE_URL',
        appendPath: '/v1',
      },
      auth: {
        target: 'env',
        name: 'OPENAI_API_KEY',
      },
      // Signed in to nothing else (a cloud sandbox), codex-acp asks its client to authenticate:
      // this signs it in to the provider as its model gateway (an API key would go to OpenAI).
      env: {
        DEFAULT_AUTH_REQUEST:
          '{"methodId":"gateway","_meta":{"gateway":{"baseUrl":"{baseUrl}","headers":{"Authorization":"Bearer {token}"},"providerName":"Crowdex"}}}',
      },
    },
    capabilities: ['models', 'reasoning', 'delegation'],
    signIn: { command: 'codex login' },
    plugin: CORE_AGENT_OWNER,
  },
  {
    id: 'opencode',
    localId: 'auto-opencode',
    name: 'OpenCode',
    family: null,
    default: true,
    discovery: {
      binaries: ['opencode'],
    },
    launch: {
      args: ['acp'],
    },
    // The native backend declares these as an OpenAI-compatible provider of OpenCode's, with the
    // models the endpoint serves; OpenCode reads the token from its environment.
    providerInjection: {
      baseUrl: {
        target: 'env',
        name: 'CROWDEX_OPENCODE_BASE_URL',
        appendPath: '/v1',
      },
      auth: {
        target: 'env',
        name: 'CROWDEX_OPENCODE_API_KEY',
      },
    },
    capabilities: ['models', 'delegation'],
    aliases: [],
    sessionConfig: [],
    signIn: { command: 'opencode auth login' },
    plugin: CORE_AGENT_OWNER,
  },
] as const satisfies readonly AcpAgentCatalogEntry[];
